import React, { useEffect, useMemo, useState } from 'react';

const BACKEND_ROOT = ((process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000')
  .trim()
  .replace(/\/api\/?$/, ''));

const INDIA_STATE_OPTIONS = [
  '', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
];

const toStateKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export default function StateInfo() {
  const [selectedState, setSelectedState] = useState('');
  const [content, setContent] = useState('');
  const [savedStates, setSavedStates] = useState([]);
  const [savedStateSearch, setSavedStateSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState({ type: '', text: '' });

  const entryCount = useMemo(() => (
    content
      .split(/[\r\n,;]+/)
      .map((item) => item.trim())
      .filter(Boolean).length
  ), [content]);

  const stateLabelByKey = useMemo(() => INDIA_STATE_OPTIONS.reduce((acc, state) => {
    if (state) acc[toStateKey(state)] = state;
    return acc;
  }, {}), []);

  const filteredSavedStates = useMemo(() => {
    const term = savedStateSearch.trim().toLowerCase();
    if (!term) return savedStates;
    return savedStates.filter((item) => {
      const label = String(item.state_label || item.file_name || item.state_key || '').toLowerCase();
      return label.includes(term);
    });
  }, [savedStates, savedStateSearch]);

  const loadSavedStates = async () => {
    try {
      const res = await fetch(`${BACKEND_ROOT}/state-info/list`);
      if (!res.ok) {
        throw new Error('Failed to load state info list');
      }
      const data = await res.json();
      setSavedStates(data.states || []);
    } catch (error) {
      console.warn('State info list unavailable:', error.message);
    }
  };

  const loadStateInfo = async (stateName) => {
    if (!stateName) {
      setContent('');
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_ROOT}/state-info?state=${encodeURIComponent(stateName)}`);
      if (!res.ok) {
        throw new Error('Failed to load state info');
      }
      const data = await res.json();
      setContent(data.content || '');
      setNotice({
        type: 'success',
        text: data.entries_count
          ? `Loaded ${data.entries_count} saved location terms for ${stateName}.`
          : `No saved entries yet for ${stateName}. Add cities, landmarks, markets, beaches, or local aliases.`,
      });
    } catch (error) {
      console.error('Failed to load state info:', error);
      setNotice({ type: 'error', text: `Could not load saved state info for ${stateName}.` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSavedStates();
  }, []);

  useEffect(() => {
    loadStateInfo(selectedState);
  }, [selectedState]);

  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setContent((prev) => {
        const trimmedPrev = prev.trim();
        return trimmedPrev ? `${trimmedPrev}\n${text.trim()}`.trim() : text.trim();
      });
      setNotice({ type: 'success', text: `Imported ${file.name}. Review and save when ready.` });
    } catch (error) {
      console.error('Failed to import file:', error);
      setNotice({ type: 'error', text: 'Could not read the uploaded file.' });
    } finally {
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!selectedState) {
      setNotice({ type: 'error', text: 'Please select a state first.' });
      return;
    }

    try {
      setSaving(true);
      const res = await fetch(`${BACKEND_ROOT}/state-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          state: selectedState,
          content,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save state info');
      }
      const data = await res.json();
      setContent((data.entries || []).join('\n'));
      setNotice({ type: 'success', text: `Saved ${data.entries_count} terms for ${selectedState}.` });
      loadSavedStates();
    } catch (error) {
      console.error('Failed to save state info:', error);
      setNotice({ type: 'error', text: error.message || 'Failed to save state info.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-shell">
      <header className="top-bar">
        <div>
          <h1 className="page-title">State Info Manager</h1>
          <p className="page-subtitle">
            Save cities, famous places, markets, landmarks, and local aliases per state so scans can detect location-relevant groups better.
          </p>
        </div>
      </header>

      {notice.text && (
        <div className={`settings-notice settings-notice-${notice.type === 'error' ? 'error' : 'success'}`}>
          {notice.text}
        </div>
      )}

      <div className="dash-grid">
        <section className="card settings-card state-info-card">
          <div className="section-head">
            <div>
              <div className="eyebrow">CONFIGURE</div>
              <h3 className="section-title">Choose State</h3>
            </div>
            <span className="pill">{entryCount} entries in editor</span>
          </div>

          <div className="settings-field-wrap">
            <label className="settings-label" htmlFor="stateSelect">State</label>
            <select
              id="stateSelect"
              className="form-input"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              {INDIA_STATE_OPTIONS.map((state) => (
                <option key={state || 'placeholder'} value={state}>
                  {state || 'Select a state'}
                </option>
              ))}
            </select>
          </div>

          <div className="settings-field-wrap">
            <label className="settings-label" htmlFor="stateInfoUpload">Upload Text File</label>
            <input
              id="stateInfoUpload"
              className="form-input"
              type="file"
              accept=".txt,.csv"
              onChange={handleFileImport}
            />
            <p className="muted">Import a text or CSV file. The contents will be appended into the editor.</p>
          </div>

          <div className="settings-field-wrap">
            <label className="settings-label" htmlFor="stateInfoContent">Cities / Famous Places / Local Terms</label>
            <textarea
              id="stateInfoContent"
              className="form-input state-info-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={'Amritsar\nLudhiana\nJalandhar\nGolden Temple\nWagah Border\nSector 17\nModel Town'}
              disabled={loading}
            />
            <p className="muted">Add one term per line. Commas and semicolons also work.</p>
          </div>

          <div className="action-row">
            <button
              type="button"
              className="primary-btn"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Save State Info'}
            </button>
          </div>
        </section>

        <section className="card settings-card state-info-card">
          <div className="section-head">
            <div>
              <div className="eyebrow">SAVED FILES</div>
              <h3 className="section-title">Available State Datasets</h3>
              <p className="muted">These files will be used during state relevance filtering in unified scans.</p>
            </div>
          </div>

          <div className="settings-field-wrap">
            <label className="settings-label" htmlFor="savedStateSearch">Search Saved States</label>
            <input
              id="savedStateSearch"
              className="form-input"
              type="text"
              value={savedStateSearch}
              onChange={(e) => setSavedStateSearch(e.target.value)}
              placeholder="Search state dataset..."
            />
          </div>

          <div className="list state-info-list-scroll">
            {filteredSavedStates.length ? filteredSavedStates.map((item) => (
              <button
                key={item.file_name}
                type="button"
                className="list-item state-info-list-btn"
                onClick={() => setSelectedState(item.state_label || stateLabelByKey[item.state_key] || item.state_key.replace(/_/g, ' '))}
                >
                  <div>
                    <div className="list-title">{item.file_name.replace('.txt', '').replace(/_/g, ' ')}</div>
                    <div className="list-sub">Saved state dataset ready to use in scan filtering.</div>
                  </div>
                  <span className="pill">Open</span>
                </button>
            )) : (
              <div className="list-item">
                <div>
                  <div className="list-title">{savedStates.length ? 'No matching state datasets' : 'No state files saved yet'}</div>
                  <div className="list-sub">
                    {savedStates.length
                      ? 'Try a different search term or clear the search box.'
                      : 'Choose a state, paste places, and save to create the first dataset.'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
